<template>
  <article class="prod-card" @click="$emit('open')">
    <div class="prod-card__media">
      <img v-if="image" :src="image" :alt="title" loading="lazy" />
      <div v-else class="prod-card__media--empty" :style="{ background: accent }">
        <i class="pi" :class="kind === 'course' ? 'pi-book' : 'pi-graduation-cap'"></i>
      </div>
      <span v-if="isOwnYear" class="prod-card__own"><i class="pi pi-star-fill"></i> سنتك الدراسية</span>
      <span v-else-if="featured" class="prod-card__featured"><i class="pi pi-star-fill"></i> مميز</span>
    </div>

    <div class="prod-card__body">
      <h3 class="prod-card__title">{{ title }}</h3>
      <p v-if="teacher" class="prod-card__teacher"><i class="pi pi-user"></i> {{ teacher }}</p>
      <p v-if="product.description" class="prod-card__desc">{{ product.description }}</p>

      <!-- Stats row: only renders whatever counts the API actually provides -->
      <div class="prod-card__stats" v-if="stats.length">
        <span v-for="s in stats" :key="s.label" class="prod-card__stat">
          <span class="prod-card__stat-icon">{{ s.icon }}</span> {{ s.value }} {{ s.label }}
        </span>
      </div>

      <!-- Compact pricing summary — the full plan list only lives on the details page -->
      <div class="prod-card__pricing">
        <div v-if="isFree" class="prod-card__free"><i class="pi pi-gift"></i> مجاني</div>
        <template v-else-if="plans.length">
          <div class="prod-card__price-block">
            <span class="prod-card__price-label">يبدأ من</span>
            <span class="prod-card__price-value">{{ money(startingPrice) }}</span>
          </div>
          <span v-if="plans.length > 1" class="prod-card__plans-badge">{{ plans.length }} خطط متاحة</span>
        </template>
        <p v-else class="prod-card__noplans">لا توجد خطط متاحة حالياً</p>
      </div>
    </div>

    <div class="prod-card__footer">
      <button class="prod-card__btn" :class="{ 'prod-card__btn--access': hasAccess }"
              :disabled="!hasAccess && !isFree && !product.accepts_subscriptions"
              @click.stop="$emit('subscribe')">
        <i class="pi" :class="hasAccess ? 'pi-sign-in' : 'pi-bolt'"></i>
        {{ ctaLabel }}
      </button>
    </div>
  </article>
</template>

<script>
import { productPlans, formatMoney, startingPriceOf } from '@/composables/subscriptionMeta.js';

export default {
  name: 'ProductCard',
  props: {
    product: { type: Object, required: true },
    kind: { type: String, default: 'course' }, // 'course' | 'academic_year'
  },
  emits: ['open', 'subscribe'],
  computed: {
    image() { return this.product.cover_image_link || this.product.image_link || null; },
    title() { return this.kind === 'course' ? this.product.title : this.product.name; },
    teacher() { return this.kind === 'course' ? this.product.teacher : null; },
    accent() { return this.product.color || '#5B5FEF'; },
    featured() { return !!this.product.featured; },
    isOwnYear() { return this.kind === 'academic_year' && !!this.product.is_students_year; },
    isFree() { return !this.product.is_subscription_enabled; },
    hasAccess() { return !!this.product.has_access; },
    plans() { return productPlans(this.product); },
    startingPrice() { return startingPriceOf(this.product); },
    // Reads optional counts if/when the API exposes them — never fabricated,
    // so today (no backend changes) this row simply doesn't render.
    stats() {
      const list = [
        { key: 'lessons_count', icon: '📚', label: 'درس' },
        { key: 'videos_count', icon: '🎥', label: 'فيديو' },
        { key: 'exams_count', icon: '📝', label: 'امتحان' },
      ];
      return list
        .filter(s => this.product[s.key] !== undefined && this.product[s.key] !== null)
        .map(s => ({ ...s, value: this.product[s.key] }));
    },
    ctaLabel() {
      if (this.hasAccess) return 'ادخل الآن';
      if (this.isFree) return 'ابدأ الآن';
      return this.product.accepts_subscriptions ? 'اشترك الآن' : 'غير متاح';
    },
  },
  methods: { money: formatMoney },
};
</script>

<style scoped>
.prod-card {
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid rgba(91,95,239,.1);
  border-radius: 18px;
  overflow: hidden;
  cursor: pointer;
  transition: transform .2s ease, box-shadow .2s ease, border-color .2s ease;
  height: 100%;
}
/* Hover lift only where a real pointer/hover exists — no ghost hover state on touch */
@media (hover: hover) and (pointer: fine) {
  .prod-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 30px rgba(91,95,239,.14);
    border-color: rgba(91,95,239,.3);
  }
}
.prod-card__media {
  position: relative;
  aspect-ratio: 16 / 9;
  background: #f1f5f9;
}
.prod-card__media img { width: 100%; height: 100%; object-fit: cover; display: block; }
.prod-card__media--empty {
  width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 2.4rem;
}
.prod-card__featured {
  position: absolute; top: 10px; inset-inline-start: 10px;
  background: rgba(255,255,255,.92); color: #b45309;
  font-size: .72rem; font-weight: 800; padding: .25rem .6rem; border-radius: 99px;
}
.prod-card__own {
  position: absolute; top: 10px; inset-inline-start: 10px;
  background: rgba(245,158,11,.95); color: #1f2937;
  font-size: .72rem; font-weight: 800; padding: .25rem .6rem; border-radius: 99px;
  display: inline-flex; align-items: center; gap: .3rem;
}
.prod-card__body { padding: 1.25rem 1.25rem .5rem; flex: 1; }
.prod-card__title {
  font-size: 1.15rem; font-weight: 800; color: #111827; margin-bottom: .4rem;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
  line-height: 1.35;
}
.prod-card__teacher { font-size: .84rem; color: #5B5FEF; font-weight: 700; margin-bottom: .45rem; }
.prod-card__desc {
  font-size: .87rem; color: #6B7280; margin-bottom: .85rem; line-height: 1.65;
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
}

/* Stats row */
.prod-card__stats { display: flex; flex-wrap: wrap; gap: .5rem; margin-bottom: .9rem; }
.prod-card__stat {
  display: inline-flex; align-items: center; gap: .3rem;
  background: rgba(91,95,239,.06); color: #4B5563;
  border-radius: 99px; padding: .3rem .65rem; font-size: .76rem; font-weight: 700;
}
.prod-card__stat-icon { font-size: .85rem; }

/* Pricing summary */
.prod-card__pricing {
  display: flex; align-items: center; flex-wrap: wrap; gap: .5rem .6rem;
  margin-bottom: .35rem; padding-top: .6rem; border-top: 1px solid rgba(17,24,39,.06);
}
.prod-card__price-block { display: flex; flex-direction: column; line-height: 1.15; }
.prod-card__price-label { font-size: .72rem; color: #94a3b8; font-weight: 600; }
.prod-card__price-value { font-size: 1.35rem; font-weight: 900; color: #5B5FEF; }
.prod-card__plans-badge {
  background: rgba(245,158,11,.12); color: #b45309; font-weight: 700;
  font-size: .72rem; padding: .25rem .55rem; border-radius: 99px; white-space: nowrap;
}
.prod-card__noplans { font-size: .78rem; color: #94a3b8; }
.prod-card__free {
  display: inline-flex; align-items: center; gap: .35rem;
  background: rgba(34,197,94,.1); color: #16a34a;
  border-radius: 8px; padding: .35rem .7rem; font-size: .82rem; font-weight: 800;
}

.prod-card__footer { padding: .7rem 1.25rem 1.25rem; }
.prod-card__btn {
  width: 100%; min-height: 50px;
  display: inline-flex; align-items: center; justify-content: center; gap: .45rem;
  background: linear-gradient(90deg, #F59E0B, #D97706);
  color: #fff; border: none; border-radius: 12px;
  padding: .7rem 1rem; font-weight: 800; font-size: .95rem; cursor: pointer;
  transition: opacity .2s ease, transform .1s ease;
}
.prod-card__btn--access { background: linear-gradient(90deg, #22c55e, #16a34a); }
@media (hover: hover) and (pointer: fine) {
  .prod-card__btn:hover:not(:disabled) { opacity: .92; }
}
.prod-card__btn:active:not(:disabled) { transform: scale(.98); }
.prod-card__btn:disabled { background: #cbd5e1; cursor: not-allowed; }

/* Mobile: near-full-bleed cards, roomier spacing, no crowding */
@media (max-width: 640px) {
  .prod-card__body { padding: 1.1rem 1.1rem .4rem; }
  .prod-card__title { font-size: 1.1rem; }
  .prod-card__price-value { font-size: 1.3rem; }
  .prod-card__footer { padding: .6rem 1.1rem 1.1rem; }
}
</style>
